/**
 * VermittlerCommissionsPage — Phase 4b.
 *
 * Zeigt dem Vermittler seine aus Fahrzeug-/Werkstatt-Rechnungen entstandenen
 * Provisionen mit Status (pending/approved/paid/rejected) und Summen-Übersicht.
 *
 * Route: /vermittler/commissions
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Loader2, Check, Clock, X as XIcon, ArrowLeft } from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../lib/auth";

const STATUS_META = {
    pending: { label: "Offen", color: "bg-amber-100 text-amber-900", icon: Clock },
    approved: { label: "Bestätigt", color: "bg-blue-100 text-blue-800", icon: Check },
    paid: { label: "Ausgezahlt", color: "bg-green-100 text-green-900", icon: Check },
    rejected: { label: "Abgelehnt", color: "bg-red-100 text-red-700", icon: XIcon },
};

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function VermittlerCommissionsPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) return;
        api.get("/vermittler/invoice-commissions")
            .then((r) => setData(r.data))
            .catch(() => setData({ items: [], summary: { count: 0, pending: 0, approved: 0, paid: 0, rejected: 0 } }))
            .finally(() => setLoading(false));
    }, [user]);

    if (!user) return <div className="p-8">Lade…</div>;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="vermittler-commissions-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/dashboard" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-dashboard">
                        <ArrowLeft className="w-3 h-3" /> Dashboard
                    </Link>
                    <div className="mt-2 flex items-center gap-3">
                        <Coins className="w-7 h-7 text-[#FFCC00]" />
                        <div>
                            <h1 className="font-display font-black text-4xl md:text-5xl">Provisionen</h1>
                            <p className="text-sm text-gray-300 mt-1">Vergütungen aus Fahrzeug- und Werkstatt-Rechnungen</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-6">
                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="commissions-summary">
                            <SummaryCard label="Offen" value={fmtEur(data.summary.pending)} testid="sum-pending" />
                            <SummaryCard label="Bestätigt" value={fmtEur(data.summary.approved)} testid="sum-approved" />
                            <SummaryCard label="Ausgezahlt" value={fmtEur(data.summary.paid)} testid="sum-paid" tone="success" />
                            <SummaryCard label="Abgelehnt" value={fmtEur(data.summary.rejected)} testid="sum-rejected" tone="muted" />
                        </div>

                        {data.items.length === 0 ? (
                            <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="commissions-empty">
                                <Coins className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                <p className="font-bold text-gray-700">Noch keine Provisionen</p>
                                <p className="text-sm text-gray-500 mt-1">Sobald ein Händler dich auf einer Fahrzeug-Rechnung als Vermittler einträgt, erscheint hier dein Provisions-Eintrag.</p>
                            </div>
                        ) : (
                            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="commissions-list">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                        <tr>
                                            <th className="text-left px-3 py-2">Datum</th>
                                            <th className="text-left px-3 py-2">Rechnung</th>
                                            <th className="text-left px-3 py-2">Händler</th>
                                            <th className="text-left px-3 py-2">Kunde</th>
                                            <th className="text-right px-3 py-2">Provision</th>
                                            <th className="text-center px-3 py-2">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.items.map((c) => {
                                            const meta = STATUS_META[c.status] || STATUS_META.pending;
                                            const Icon = meta.icon;
                                            return (
                                                <tr key={c.id} className="border-t border-gray-100" data-testid={`commission-row-${c.id}`}>
                                                    <td className="px-3 py-2 text-xs text-gray-600 font-mono-num">
                                                        {new Date(c.created_at).toLocaleDateString("de-DE")}
                                                    </td>
                                                    <td className="px-3 py-2 text-xs font-bold">{c.invoice_number || "—"}
                                                        <span className="block text-[10px] text-gray-500 font-normal">{c.source === "workshop_invoice" ? "Werkstatt" : "Fahrzeug"}</span>
                                                    </td>
                                                    <td className="px-3 py-2 text-xs">{c.dealer_label}</td>
                                                    <td className="px-3 py-2 text-xs">{c.customer_label}</td>
                                                    <td className="px-3 py-2 text-right font-mono-num font-bold text-base">{fmtEur(c.amount_eur)}</td>
                                                    <td className="px-3 py-2 text-center">
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${meta.color}`} data-testid={`commission-status-${c.id}`}>
                                                            <Icon className="w-3 h-3" /> {meta.label}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function SummaryCard({ label, value, tone = "default", testid }) {
    const tones = {
        default: "bg-white border-gray-200",
        success: "bg-green-50 border-green-200",
        muted: "bg-gray-50 border-gray-200",
    };
    return (
        <div className={`border rounded-sm p-4 ${tones[tone] || tones.default}`} data-testid={testid}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">{label}</div>
            <div className="font-display font-black text-xl text-[#111827] font-mono-num leading-none">{value}</div>
        </div>
    );
}
